/**
 * legalContent.js — Texto de la Política de Privacidad y los Términos del Servicio.
 *
 * Viven fuera de src/i18n/translations porque son documentos largos y con vida propia
 * (fecha de vigencia, revisión legal) que no tienen por qué engordar los archivos de
 * traducción de la UI.
 *
 * Se publican en rutas PÚBLICAS (/privacy y /terms, fuera del Authenticator): TikTok
 * exige que la Privacy Policy y los Terms of Service sean visibles en el sitio para
 * aprobar la app. Ver docs/tiktok_app_review.md.
 *
 * REVISAR ANTES DE PUBLICAR: los tres valores de abajo salen en los documentos.
 */
export const LEGAL_ENTITY = 'Data Bunker';
export const CONTACT_EMAIL = 'contacto@data-bunker.ai';   // buzón que atiende derechos y dudas
export const APP_URL = 'https://media.data-bunker.ai';

const EFFECTIVE = { es: '12 de agosto de 2026', en: 'August 12, 2026' };

// ── Español ─────────────────────────────────────────────────────────────────
const ES_PRIVACY = {
  title: 'Aviso de Privacidad',
  updated: `Vigente desde el ${EFFECTIVE.es}`,
  lead: `${LEGAL_ENTITY} opera la plataforma de analítica de marketing disponible en ${APP_URL} ` +
    '(la "Plataforma"), un servicio empresarial que usan marcas y sus agencias para medir el ' +
    'desempeño de sus propios canales de marketing. Este aviso explica qué datos tratamos, con ' +
    'qué finalidad y qué control tienes sobre ellos.',
  sections: [
    {
      title: '1. A quién aplica este aviso',
      paragraphs: [
        'Aplica a las personas que acceden a la Plataforma con credenciales otorgadas por su ' +
        'organización y a las cuentas de plataformas de terceros que esas organizaciones deciden ' +
        'conectar. La Plataforma no está abierta al público general ni admite registro libre.',
      ],
    },
    {
      title: '2. Datos que tratamos',
      paragraphs: ['Tratamos dos conjuntos de datos, y ninguno más:'],
      bullets: [
        'Datos de la cuenta de usuario: nombre de usuario, correo electrónico, organización, rol, ' +
        'idioma preferido y registros de acceso y actividad dentro de la Plataforma, que usamos ' +
        'para seguridad, soporte y facturación. La autenticación la gestiona Amazon Cognito: no ' +
        'almacenamos contraseñas.',
        'Datos de las plataformas conectadas: únicamente cuando un usuario autorizado conecta una ' +
        'cuenta de la que su organización es titular. Hoy soportamos TikTok, Meta (Facebook e ' +
        'Instagram), Google Analytics y Google Ads.',
      ],
    },
    {
      title: '3. Datos que obtenemos de TikTok',
      paragraphs: [
        'Cuando el titular de una cuenta de TikTok Business autoriza la conexión, accedemos ' +
        'mediante las APIs oficiales de TikTok, y solo de esa cuenta, a:',
      ],
      bullets: [
        'El perfil público de la cuenta: nombre de usuario, nombre visible, foto de perfil, número ' +
        'de seguidores y seguidos, total de "me gusta" y número de videos publicados.',
        'Las métricas agregadas de la cuenta en el periodo consultado: reproducciones de video, ' +
        'visitas al perfil, "me gusta", comentarios, veces compartido y alcance.',
        'Los videos publicados por esa misma cuenta, con su miniatura, descripción, fecha, enlace ' +
        'público y métricas por video.',
        'Los comentarios públicos publicados en esos videos propios (texto, nombre de usuario de ' +
        'quien comenta, "me gusta" y fecha), que se consultan solo cuando el usuario abre un video ' +
        'concreto dentro de la Plataforma.',
      ],
      note: 'No solicitamos ni tratamos mensajes directos, datos de contacto, ubicación ni ' +
        'información de cuentas que no hayan autorizado expresamente la conexión.',
    },
    {
      title: '4. Para qué usamos estos datos',
      bullets: [
        'Mostrar al titular de la cuenta la analítica de su propio contenido dentro de la Plataforma.',
        'Generar los reportes y exportaciones que la propia organización solicita.',
        'Operar, dar soporte y proteger la Plataforma (diagnóstico, prevención de abuso y auditoría).',
      ],
      note: 'No vendemos, alquilamos ni cedemos estos datos. No los usamos para publicidad ni ' +
        'segmentación. No entrenamos modelos de inteligencia artificial con datos personales ' +
        'obtenidos de TikTok. No construimos perfiles de usuarios finales de TikTok ni cruzamos ' +
        'esos datos con otras fuentes para identificar a las personas que comentan.',
    },
    {
      title: '5. Base del tratamiento',
      paragraphs: [
        'Tratamos los datos de las plataformas conectadas con base en la autorización explícita que ' +
        'el titular otorga mediante el flujo de consentimiento de cada plataforma (OAuth), y los ' +
        'datos de cuenta de usuario con base en la relación contractual con la organización que ' +
        'contrata el servicio.',
      ],
    },
    {
      title: '6. Con quién se comparten',
      paragraphs: [
        'No compartimos los datos con terceros con fines comerciales. Únicamente los procesan ' +
        'nuestros proveedores de infraestructura, en calidad de encargados y bajo obligación de ' +
        'confidencialidad:',
      ],
      bullets: [
        'Amazon Web Services (cómputo, almacenamiento, identidad y modelos de lenguaje), en ' +
        'regiones de Estados Unidos.',
        'Nuestro proveedor de base de datos PostgreSQL gestionada.',
      ],
      note: 'También podríamos revelar información cuando una autoridad competente lo requiera ' +
        'conforme a la ley aplicable.',
    },
    {
      title: '7. Conservación y eliminación',
      bullets: [
        'Los tokens de acceso a las plataformas conectadas se guardan cifrados del lado del ' +
        'servidor y nunca se exponen al navegador.',
        'Al desconectar una integración, el token se elimina de inmediato y los datos derivados de ' +
        'esa plataforma se eliminan en un plazo máximo de 30 días.',
        'Los datos de cuenta de usuario se conservan mientras dure la relación con la organización, ' +
        'más el plazo que exija la ley aplicable.',
        `También puedes solicitar la eliminación escribiendo a ${CONTACT_EMAIL}.`,
      ],
    },
    {
      title: '8. Cómo revocar el acceso',
      paragraphs: [
        'Puedes retirar la autorización en cualquier momento, sin costo y sin necesidad de ' +
        'justificarla, por dos vías:',
      ],
      bullets: [
        'Desde la Plataforma: menú Integraciones → tarjeta de la plataforma correspondiente → ' +
        'Desconectar.',
        'Desde TikTok: Perfil → Configuración y privacidad → Seguridad y permisos → Administrar ' +
        'permisos de aplicaciones, y retira el acceso a nuestra aplicación.',
      ],
      note: 'Al revocar, la sincronización se detiene de inmediato y aplica el plazo de eliminación ' +
        'descrito en el apartado anterior.',
    },
    {
      title: '9. Seguridad',
      paragraphs: [
        'Ciframos la información en tránsito (TLS) y en reposo, aislamos los datos por organización, ' +
        'restringimos el acceso al personal que lo necesita, ofrecemos autenticación multifactor y ' +
        'mantenemos registros de auditoría de los accesos y las acciones relevantes.',
      ],
    },
    {
      title: '10. Transferencias internacionales',
      paragraphs: [
        'La infraestructura de la Plataforma está alojada en Estados Unidos. Al usar el servicio, ' +
        'aceptas que la información se procese en ese país con las salvaguardas contractuales y ' +
        'técnicas descritas en este aviso.',
      ],
    },
    {
      title: '11. Tus derechos',
      paragraphs: [
        `Puedes solicitar el acceso, la rectificación, la cancelación o la oposición al tratamiento ` +
        `de tus datos, así como la portabilidad de los mismos, escribiendo a ${CONTACT_EMAIL}. ` +
        'Responderemos en un plazo máximo de 20 días hábiles.',
      ],
    },
    {
      title: '12. Menores de edad',
      paragraphs: [
        'La Plataforma es un servicio empresarial dirigido exclusivamente a mayores de 18 años. No ' +
        'recabamos de forma consciente datos de menores de edad.',
      ],
    },
    {
      title: '13. Cumplimiento de las políticas de las plataformas conectadas',
      paragraphs: [
        'Nos ajustamos a los términos para desarrolladores y a las políticas de las plataformas ' +
        'cuyas APIs utilizamos, incluidos los TikTok Developer Terms of Service. Si una plataforma ' +
        'revoca nuestro acceso o modifica sus reglas, dejamos de tratar los datos obtenidos a ' +
        'través de ella y los eliminamos conforme al apartado 7.',
      ],
    },
    {
      title: '14. Cambios a este aviso',
      paragraphs: [
        'Publicaremos cualquier modificación en esta misma página, con una nueva fecha de vigencia. ' +
        'Si el cambio es sustancial, lo notificaremos a las organizaciones clientes.',
      ],
    },
    {
      title: '15. Contacto',
      paragraphs: [
        `Para cualquier duda sobre este aviso o para ejercer tus derechos, escríbenos a ${CONTACT_EMAIL}.`,
      ],
    },
  ],
};

const ES_TERMS = {
  title: 'Términos del Servicio',
  updated: `Vigentes desde el ${EFFECTIVE.es}`,
  lead: `Estos términos rigen el acceso y uso de la plataforma de analítica de marketing de ` +
    `${LEGAL_ENTITY} disponible en ${APP_URL} (la "Plataforma"). Al acceder, aceptas quedar ` +
    'obligado por ellos. Si no estás de acuerdo, no uses el servicio.',
  sections: [
    {
      title: '1. Qué es la Plataforma',
      paragraphs: [
        'Un servicio empresarial que consolida en un solo lugar los datos de marketing de una marca ' +
        '—campañas pagadas, analítica web y las cuentas de redes sociales de la propia marca— para ' +
        'que sus equipos midan el desempeño de sus canales y generen reportes.',
      ],
    },
    {
      title: '2. Cuentas y acceso',
      bullets: [
        'Las credenciales son nominativas: no deben compartirse ni cederse a terceros.',
        'Cada organización es responsable del uso que hagan las personas a las que otorga acceso.',
        'Debes avisarnos de inmediato si detectas un acceso no autorizado a tu cuenta.',
      ],
    },
    {
      title: '3. Integraciones con plataformas de terceros',
      bullets: [
        'Al conectar una cuenta de TikTok, Meta, Google u otra plataforma, declaras ser su titular o ' +
        'contar con autorización expresa del titular para hacerlo.',
        'El acceso a esos datos depende de las APIs y las políticas de cada plataforma. Podemos ' +
        'suspender o modificar una integración si la plataforma cambia sus reglas o revoca el acceso.',
        'Las métricas que muestra la Plataforma provienen de esas APIs y pueden diferir de las que ' +
        'muestran las aplicaciones nativas por diferencias de metodología, ventanas de atribución o ' +
        'momento de consulta.',
        'El uso de esos datos queda además sujeto a los términos de la plataforma de origen, ' +
        'incluidos los TikTok Developer Terms of Service.',
      ],
    },
    {
      title: '4. Uso aceptable',
      paragraphs: ['No está permitido usar la Plataforma para:'],
      bullets: [
        'Obtener datos de cuentas que no sean tuyas o para las que no tengas autorización.',
        'Revender, redistribuir o ceder a terceros los datos obtenidos a través de las integraciones.',
        'Realizar ingeniería inversa, eludir los controles de acceso o interferir con el servicio.',
        'Cualquier fin que infrinja la ley aplicable o las políticas de las plataformas conectadas.',
      ],
    },
    {
      title: '5. Propiedad intelectual',
      paragraphs: [
        `El software, la interfaz y la documentación de la Plataforma son propiedad de ${LEGAL_ENTITY}. ` +
        'Los datos de tu organización siguen siendo tuyos; nos concedes únicamente la licencia ' +
        'limitada necesaria para procesarlos y prestarte el servicio.',
      ],
    },
    {
      title: '6. Disponibilidad y exactitud',
      paragraphs: [
        'La Plataforma se presta "tal cual". No garantizamos disponibilidad ininterrumpida ni la ' +
        'exactitud absoluta de las métricas que entregan las APIs de terceros. Las decisiones ' +
        'comerciales que tomes con base en la información mostrada son de tu responsabilidad y ' +
        'deben validarse antes de ejecutarse.',
      ],
    },
    {
      title: '7. Limitación de responsabilidad',
      paragraphs: [
        'En la medida que permita la ley aplicable, no seremos responsables por daños indirectos, ' +
        'incidentales o consecuenciales, ni por lucro cesante o pérdida de datos derivados del uso ' +
        'o la imposibilidad de uso de la Plataforma.',
      ],
    },
    {
      title: '8. Vigencia y terminación',
      paragraphs: [
        'Estos términos aplican mientras exista la relación de servicio. Podemos suspender o ' +
        'terminar el acceso ante un incumplimiento grave o cuando lo exija una plataforma conectada ' +
        'o una autoridad competente. Al terminar, aplican los plazos de eliminación del Aviso de ' +
        'Privacidad.',
      ],
    },
    {
      title: '9. Modificaciones',
      paragraphs: [
        'Podemos actualizar estos términos publicando la nueva versión en esta página, con su fecha ' +
        'de vigencia. El uso posterior del servicio implica su aceptación.',
      ],
    },
    {
      title: '10. Ley aplicable',
      paragraphs: [
        'Estos términos se rigen por las leyes de los Estados Unidos Mexicanos. Para su ' +
        'interpretación y cumplimiento, las partes se someten a los tribunales competentes de la ' +
        'Ciudad de México, renunciando a cualquier otro fuero.',
      ],
    },
    {
      title: '11. Contacto',
      paragraphs: [`Dudas sobre estos términos: ${CONTACT_EMAIL}.`],
    },
  ],
};

// ── English ─────────────────────────────────────────────────────────────────
const EN_PRIVACY = {
  title: 'Privacy Policy',
  updated: `Effective ${EFFECTIVE.en}`,
  lead: `${LEGAL_ENTITY} operates the marketing analytics platform available at ${APP_URL} ` +
    '(the "Platform"), a business service used by brands and their agencies to measure the ' +
    'performance of their own marketing channels. This policy explains what data we process, why, ' +
    'and what control you have over it.',
  sections: [
    {
      title: '1. Who this policy covers',
      paragraphs: [
        'It covers people who access the Platform with credentials issued by their organization, ' +
        'and the third-party platform accounts those organizations choose to connect. The Platform ' +
        'is not open to the general public and does not offer self-service sign-up.',
      ],
    },
    {
      title: '2. Data we process',
      paragraphs: ['We process two sets of data, and nothing else:'],
      bullets: [
        'User account data: username, email address, organization, role, language preference, and ' +
        'sign-in and activity logs within the Platform, used for security, support and billing. ' +
        'Authentication is handled by Amazon Cognito: we do not store passwords.',
        'Connected platform data: only when an authorized user connects an account their ' +
        'organization owns. We currently support TikTok, Meta (Facebook and Instagram), Google ' +
        'Analytics and Google Ads.',
      ],
    },
    {
      title: '3. Data we obtain from TikTok',
      paragraphs: [
        'When the owner of a TikTok Business account authorizes the connection, we access — through ' +
        "TikTok's official APIs, and only for that account:",
      ],
      bullets: [
        'The public account profile: username, display name, profile picture, follower and ' +
        'following counts, total likes and number of published videos.',
        'Aggregated account metrics for the selected period: video views, profile views, likes, ' +
        'comments, shares and reach.',
        'The videos published by that same account, with their thumbnail, caption, date, public ' +
        'link and per-video metrics.',
        'Public comments posted on those own videos (text, commenter username, likes and date), ' +
        'retrieved only when the user opens a specific video inside the Platform.',
      ],
      note: 'We do not request or process direct messages, contact details, location data, or ' +
        'information from accounts that have not expressly authorized the connection.',
    },
    {
      title: '4. How we use this data',
      bullets: [
        "To show the account owner analytics about their own content inside the Platform.",
        'To generate the reports and exports the organization itself requests.',
        'To operate, support and protect the Platform (troubleshooting, abuse prevention, auditing).',
      ],
      note: 'We do not sell, rent or trade this data. We do not use it for advertising or targeting. ' +
        'We do not train artificial intelligence models on personal data obtained from TikTok. We do ' +
        'not build profiles of TikTok end users, nor do we combine this data with other sources to ' +
        'identify the people who comment.',
    },
    {
      title: '5. Legal basis',
      paragraphs: [
        'We process connected platform data on the basis of the explicit authorization the account ' +
        "owner grants through each platform's consent flow (OAuth), and user account data on the " +
        'basis of our contract with the subscribing organization.',
      ],
    },
    {
      title: '6. Who we share it with',
      paragraphs: [
        'We do not share this data with third parties for commercial purposes. It is processed only ' +
        'by our infrastructure providers, acting as processors under confidentiality obligations:',
      ],
      bullets: [
        'Amazon Web Services (compute, storage, identity and language models), in United States regions.',
        'Our managed PostgreSQL database provider.',
      ],
      note: 'We may also disclose information where a competent authority requires it under ' +
        'applicable law.',
    },
    {
      title: '7. Retention and deletion',
      bullets: [
        'Access tokens for connected platforms are stored encrypted server-side and are never ' +
        'exposed to the browser.',
        'When an integration is disconnected, the token is deleted immediately and data derived ' +
        'from that platform is deleted within 30 days at most.',
        'User account data is retained for the duration of the relationship with the organization, ' +
        'plus any period required by applicable law.',
        `You may also request deletion by writing to ${CONTACT_EMAIL}.`,
      ],
    },
    {
      title: '8. How to revoke access',
      paragraphs: [
        'You can withdraw your authorization at any time, free of charge and without giving reasons, ' +
        'in two ways:',
      ],
      bullets: [
        'From the Platform: Integrations menu → the relevant platform card → Disconnect.',
        'From TikTok: Profile → Settings and privacy → Security and permissions → Manage app ' +
        'permissions, and remove access for our application.',
      ],
      note: 'Upon revocation, synchronization stops immediately and the deletion timeline described ' +
        'above applies.',
    },
    {
      title: '9. Security',
      paragraphs: [
        'We encrypt data in transit (TLS) and at rest, isolate data per organization, restrict ' +
        'access to personnel who need it, offer multi-factor authentication, and keep audit logs of ' +
        'access and relevant actions.',
      ],
    },
    {
      title: '10. International transfers',
      paragraphs: [
        'The Platform infrastructure is hosted in the United States. By using the service you agree ' +
        'that information is processed in that country under the contractual and technical ' +
        'safeguards described in this policy.',
      ],
    },
    {
      title: '11. Your rights',
      paragraphs: [
        `You may request access to, correction, deletion of, or objection to the processing of your ` +
        `data, as well as data portability, by writing to ${CONTACT_EMAIL}. We respond within 20 ` +
        'business days at most.',
      ],
    },
    {
      title: '12. Minors',
      paragraphs: [
        'The Platform is a business service intended exclusively for people aged 18 or older. We do ' +
        'not knowingly collect data from minors.',
      ],
    },
    {
      title: '13. Compliance with connected platform policies',
      paragraphs: [
        "We comply with the developer terms and policies of the platforms whose APIs we use, " +
        'including the TikTok Developer Terms of Service. If a platform revokes our access or ' +
        'changes its rules, we stop processing the data obtained through it and delete it as ' +
        'described in section 7.',
      ],
    },
    {
      title: '14. Changes to this policy',
      paragraphs: [
        'We will post any changes on this page with a new effective date. If a change is material, ' +
        'we will notify our client organizations.',
      ],
    },
    {
      title: '15. Contact',
      paragraphs: [
        `For any question about this policy, or to exercise your rights, write to ${CONTACT_EMAIL}.`,
      ],
    },
  ],
};

const EN_TERMS = {
  title: 'Terms of Service',
  updated: `Effective ${EFFECTIVE.en}`,
  lead: `These terms govern access to and use of the ${LEGAL_ENTITY} marketing analytics platform ` +
    `available at ${APP_URL} (the "Platform"). By accessing it, you agree to be bound by them. If ` +
    'you do not agree, do not use the service.',
  sections: [
    {
      title: '1. What the Platform is',
      paragraphs: [
        "A business service that consolidates a brand's marketing data — paid campaigns, web " +
        "analytics and the brand's own social media accounts — in one place, so its teams can " +
        'measure channel performance and produce reports.',
      ],
    },
    {
      title: '2. Accounts and access',
      bullets: [
        'Credentials are personal: they must not be shared with or transferred to third parties.',
        'Each organization is responsible for the use made by the people it grants access to.',
        'You must notify us immediately if you detect unauthorized access to your account.',
      ],
    },
    {
      title: '3. Third-party platform integrations',
      bullets: [
        'By connecting a TikTok, Meta, Google or other platform account, you represent that you own ' +
        "it or have the owner's express authorization to do so.",
        "Access to that data depends on each platform's APIs and policies. We may suspend or change " +
        'an integration if the platform changes its rules or revokes access.',
        'Metrics shown in the Platform come from those APIs and may differ from what native apps ' +
        'display, due to methodology, attribution windows or query timing.',
        'Use of that data is additionally subject to the terms of the source platform, including the ' +
        'TikTok Developer Terms of Service.',
      ],
    },
    {
      title: '4. Acceptable use',
      paragraphs: ['You may not use the Platform to:'],
      bullets: [
        'Obtain data from accounts that are not yours or that you are not authorized to access.',
        'Resell, redistribute or transfer to third parties the data obtained through the integrations.',
        'Reverse engineer, circumvent access controls or interfere with the service.',
        'Any purpose that breaches applicable law or the policies of the connected platforms.',
      ],
    },
    {
      title: '5. Intellectual property',
      paragraphs: [
        `The Platform's software, interface and documentation are the property of ${LEGAL_ENTITY}. ` +
        'Your organization\'s data remains yours; you grant us only the limited license needed to ' +
        'process it and provide the service.',
      ],
    },
    {
      title: '6. Availability and accuracy',
      paragraphs: [
        'The Platform is provided "as is". We do not guarantee uninterrupted availability or the ' +
        'absolute accuracy of metrics delivered by third-party APIs. Business decisions you make ' +
        'based on the information shown are your responsibility and should be validated before ' +
        'acting on them.',
      ],
    },
    {
      title: '7. Limitation of liability',
      paragraphs: [
        'To the extent permitted by applicable law, we are not liable for indirect, incidental or ' +
        'consequential damages, nor for lost profits or data loss arising from the use of, or ' +
        'inability to use, the Platform.',
      ],
    },
    {
      title: '8. Term and termination',
      paragraphs: [
        'These terms apply for as long as the service relationship exists. We may suspend or ' +
        'terminate access in case of material breach, or where a connected platform or competent ' +
        'authority requires it. On termination, the deletion timelines in the Privacy Policy apply.',
      ],
    },
    {
      title: '9. Changes',
      paragraphs: [
        'We may update these terms by publishing the new version on this page with its effective ' +
        'date. Continued use of the service constitutes acceptance.',
      ],
    },
    {
      title: '10. Governing law',
      paragraphs: [
        'These terms are governed by the laws of the United Mexican States. For their ' +
        'interpretation and enforcement, the parties submit to the competent courts of Mexico City, ' +
        'waiving any other jurisdiction.',
      ],
    },
    {
      title: '11. Contact',
      paragraphs: [`Questions about these terms: ${CONTACT_EMAIL}.`],
    },
  ],
};

const DOCS = {
  es: { privacy: ES_PRIVACY, terms: ES_TERMS },
  en: { privacy: EN_PRIVACY, terms: EN_TERMS },
};

/** Idioma efectivo del documento. `pt` (y cualquier otro) cae a inglés: preferimos un
 *  texto legal íntegro en otro idioma a una traducción a medias. */
export function resolveLegalLang(language) {
  return DOCS[language] ? language : 'en';
}

/** Documento legal ('privacy' | 'terms') en el idioma efectivo. */
export function getLegalDoc(kind, language) {
  return DOCS[resolveLegalLang(language)][kind];
}

export const LEGAL_LANGS = [
  { id: 'es', label: 'Español' },
  { id: 'en', label: 'English' },
];
